import {
  Box,
  Button,
  Container,
  chakra,
  Flex,
  Heading,
  Text,
  Image,
  HStack,
  Link,
  useToken,
  DrawerRoot,
  DrawerBackdrop,
  DrawerPositioner,
  DrawerContent,
  DrawerBody,
} from "@chakra-ui/react";
import { ModalPage } from "../../../components/ModalPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faCalendar,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faLocationDot,
  faShareNodes,
  faUser,
  faXmark,
  faTicketSimple,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import { SparksChip } from "../../../components/SparksChip/SparksChip";
import { SpotSavedWidget } from "../../../components/SpotSavedWidget/SpotSavedWidget";
import { inviteErrorMessage } from "../../../components/InviteSparkFlow/inviteSparkShared";
import { SpotWaitlistedWidget } from "../../../components/SpotWaitlistedWidget/SpotWaitlistedWidget";
import { CheckInCodeSheet } from "../../../components/CheckInCodeSheet/CheckInCodeSheet";
import { EventArrivalSection } from "../../../components/EventArrivalSection/EventArrivalSection";
import { AfterEventMatchingCard } from "../../../components/AfterEventMatchingCard/AfterEventMatchingCard";
import { RegistrationReceipt } from "../../../components/RegistrationReceipt/RegistrationReceipt";
import { RegisteredEventBar } from "../../../components/RegisteredEventBar/RegisteredEventBar";
import { PinnedBottomBar } from "../../../components/PinnedBottomBar/PinnedBottomBar";
import { googleMapsLink } from "../../../lib/googleMapsLink";
import { sparkMapEmbedUrl } from "../../../lib/sparkMapEmbedUrl";
import { useInView } from "../../../lib/useInView";
import { useMyRegistration } from "../../../lib/useMyRegistration";
import { useEventPreSpark } from "../../../lib/useEventPreSpark";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  activeSparkDiscount,
  getAgeRangeString,
  getDateTimeRangeString,
  SparkDiscount,
  sparkShareImage,
  SparkWithRegistrationSummary,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../../lib/apiReq";
import { useStoreData, store, refreshUserState } from "../../../lib/store/store";
import { isSparkFullFor } from "../../../lib/sparkHelpers";
import { primaryProfileImagePath } from "../../../lib/profileMedia";
import { useKeyboardInset } from "../../../lib/useKeyboardInset";
import { SparkRegisterForm } from "../../../components/SparkRegisterForm/SparkRegisterForm";
import { CompleteProfileReminderDialog } from "../../../components/CompleteProfileReminderDialog/CompleteProfileReminderDialog";
import { InviteSparkAfterRegisterDialog } from "../../../components/InviteSparkAfterRegisterDialog/InviteSparkAfterRegisterDialog";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { StandardMarkdown } from "../../../components/StandardMarkdown/StandardMarkdown";
import { SectionDivider } from "../../../components/SectionDivider/SectionDivider";
import { Meta } from "../../../components/Meta/Meta";
import mobile from "is-mobile";
import { isApplePlatform } from "../../../lib/mobileHelpers";
import { toaster } from "../../../lib/toaster";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * A published event always has a price and a date. A draft previewed by
 * Green Flag or by the partner whose event it is may have neither yet, so
 * the page says so rather than printing "$null" or "Invalid Date".
 */
const priceLabel = (price: number | null | undefined) =>
  price == null ? "Price to be confirmed" : `$${price}`;
const NO_DATE_YET = "Date to be confirmed";

function AccordionSection({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [greenDark] = useToken("colors", ["brand.green.dark"]);

  return (
    <Box borderTop="1px solid" borderColor="brand.green.dark">
      <Flex
        as="button"
        w="100%"
        bg="transparent"
        border="0"
        textAlign="left"
        align="center"
        cursor="pointer"
        gap="1rem"
        py="1rem"
        onClick={() => setOpen(!open)}
      >
        <Text flex={1} textStyle="bodyLg">
          {title}
        </Text>
        <Box
          transition="transform 0.2s ease-in-out"
          transform={open ? "rotate(180deg)" : "rotate(0deg)"}
          color={greenDark}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </Box>
      </Flex>
      <Box
        maxH={open ? "64rem" : "0"}
        overflow="hidden"
        transition="max-height 0.3s ease-out"
      >
        <Box pb="1rem">{children}</Box>
      </Box>
    </Box>
  );
}

export function EventV2() {
  const location = useLocation();
  const isMobile = useMemo(() => mobile(), []);
  const { sparkId } = useParams();
  const entryState = location.state as {
    animate?: boolean;
    // Set when the viewer arrived here via the "Sign up for this event"
    // prompt in the invite flow — once they register, send the invite.
    inviteSparkId?: number;
    inviteSparkName?: string;
  } | null;
  const animateEntrance = entryState?.animate === true;
  const navigate = useNavigate();
  const pendingInvite = useMemo(
    () =>
      entryState?.inviteSparkId
        ? {
            sparkId: entryState.inviteSparkId,
            name: entryState.inviteSparkName || "your spark",
          }
        : null,
    [entryState?.inviteSparkId, entryState?.inviteSparkName],
  );
  const [inviteReq] = useReq(apiReq);
  // Guard so a re-registration in the same session doesn't re-send the invite.
  const invitedRef = useRef(false);

  // After the viewer registers for this event, fulfill the invite intent they
  // arrived with (from the "Sign up for this event" prompt in the invite flow)
  // and toast the outcome.
  const sendPendingInviteAfterRegister = useCallback(
    async (eventSparkId: number) => {
      if (!pendingInvite || invitedRef.current) return;
      invitedRef.current = true;
      const response = await inviteReq.post("/user/spark-invitations", {
        invitedUserId: pendingInvite.sparkId,
        sparkId: eventSparkId,
      });
      if (response.success) {
        toaster.create({
          type: "success",
          title: `${pendingInvite.name} was invited to this event!`,
        });
      } else {
        toaster.create({
          type: "error",
          title: inviteErrorMessage(response.error?.code, pendingInvite.name),
        });
      }
      // Clear the intent so a refresh / re-register won't re-send it.
      navigate(location.pathname, {
        replace: true,
        state: { animate: false },
      });
    },
    [pendingInvite, inviteReq, navigate, location.pathname],
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // On mobile web the virtual keyboard slides up over the bottom-anchored
  // drawer; offset the positioner above the keyboard so its fields stay visible.
  const keyboardInset = useKeyboardInset();
  const [profileReminderShown, setProfileReminderShown] = useState(false);
  const [invitePromptOpen, setInvitePromptOpen] = useState(false);
  // Two prompts want the screen the instant a registration lands, and they
  // queue rather than stack: the invite prompt is about THIS event and is only
  // relevant for a moment, so it goes first and the profile nudge waits here
  // until it's dismissed. A ref, not state, because the queued decision is
  // made inside the register callback and read back from another callback —
  // nothing renders off it.
  const profileReminderPendingRef = useRef(false);
  const showProfileReminderIfPending = useCallback(() => {
    if (!profileReminderPendingRef.current) return;
    profileReminderPendingRef.current = false;
    setProfileReminderShown(true);
  }, []);
  const closeInvitePrompt = useCallback(() => {
    setInvitePromptOpen(false);
    showProfileReminderIfPending();
  }, [showProfileReminderIfPending]);
  const [sparkReq, sparkReqState] =
    useReq<SparkWithRegistrationSummary>(apiReq);
  const storeData = useStoreData();

  useEffect(() => {
    sparkReq.get(`/spark/${sparkId}`);
  }, [sparkId, sparkReq]);

  const refresh = useCallback(() => {
    if (store.data.auth.token) {
      refreshUserState();
    }
    sparkReq.get(`/spark/${sparkId}`);
  }, [sparkId, sparkReq]);

  const registrationType = useMemo(() => {
    const reg = storeData.upcomingSparks?.find(
      (s) => s.id === parseInt(sparkId || ""),
    );
    if (!reg || (!reg.registered && !reg.waitlist)) {
      return "unregistered";
    } else if (reg.waitlist) {
      return "waitlist";
    } else {
      return "registered";
    }
  }, [storeData.upcomingSparks, sparkId]);

  const isSparkFull =
    !!storeData.user?.gender &&
    !!sparkReqState.result &&
    isSparkFullFor(storeData.user?.gender, sparkReqState.result);

  const spark = sparkReqState.result;

  const {
    registration: myRegistration,
    checkInOpen,
    openCheckIn,
    closeCheckIn,
  } = useMyRegistration(sparkId, registrationType === "registered");

  // The pinned check-in bar only shows once the card holding the code row
  // has scrolled out of view, so the two never sit on screen together.
  const [spotSavedEl, setSpotSavedEl] = useState<HTMLDivElement | null>(null);
  const spotSavedInView = useInView(spotSavedEl);

  const { eventSoon, preSpark, showPreSpark, startSparking } = useEventPreSpark(
    parseInt(sparkId || "") || undefined,
    spark?.starts_at,
    registrationType === "registered",
  );

  const upcomingSparkMatch = useMemo(() => {
    const sparkIdNum = parseInt(sparkId || "");
    return storeData.upcomingSparks?.find((s) => s.id === sparkIdNum);
  }, [storeData.upcomingSparks, sparkId]);

  const attendingLikedCount = upcomingSparkMatch?.attendingLikedCount ?? 0;

  const sparkAvatars = useMemo(() => {
    return upcomingSparkMatch
      ? upcomingSparkMatch.attendingLikedAvatars.map((a) => a.url)
      : [];
  }, [upcomingSparkMatch]);

  // Mutual Sparks who invited the viewer to this event — shown un-blurred and
  // named ("{name} invited you") leading the attending chip. The server
  // excludes these inviters from `attendingLikedCount`, so the crowd line
  // ("X other sparks attending") never double-counts them.
  const inviters = useMemo(
    () =>
      (storeData.incomingInvitations ?? [])
        .filter((i) => i.event.sparkId === parseInt(sparkId || ""))
        .map((i) => ({
          name: i.inviter.firstName ?? "A Spark",
          path: primaryProfileImagePath(i.inviter),
        })),
    [storeData.incomingInvitations, sparkId],
  );

  const dateTimeRange = useMemo(() => {
    if (!spark) return { date: "", time: "" };
    if (!spark.starts_at || !spark.ends_at) return { date: NO_DATE_YET, time: "" };
    const rangeString = getDateTimeRangeString(
      dayjs(spark.starts_at),
      dayjs(spark.ends_at),
    );
    const match = rangeString.match(/^(.+?)\s+(\d+:\d+.*)$/);
    if (match) {
      return { date: match[1], time: match[2] };
    }
    return { date: rangeString, time: "" };
  }, [spark]);

  const [overviewExpanded, setOverviewExpanded] = useState(false);
  const [activeDiscount, setActiveDiscount] = useState<SparkDiscount | null>(
    null,
  );

  useEffect(() => {
    const discount = activeSparkDiscount({ discount: spark?.discount ?? null });
    setActiveDiscount(discount);
    // Drop it the moment it ends, so a page left open stops quoting it.
    if (discount?.expires_at) {
      const timer = setTimeout(
        () => setActiveDiscount(null),
        dayjs(discount.expires_at).diff(dayjs()),
      );
      return () => clearTimeout(timer);
    }
  }, [spark?.discount]);

  // Parse description into sections by splitting on markdown headings
  const descriptionSections = useMemo(() => {
    if (!spark?.description) return { whatToExpect: "" };
    const text = spark.description
      .replace(
        /\{AGE_RANGE\}/g,
        getAgeRangeString(spark.min_age, spark.max_age, spark.age_range_label).text,
      )
      .replace(
        /\{TIME_RANGE\}/g,
        spark.starts_at && spark.ends_at
          ? getDateTimeRangeString(dayjs(spark.starts_at), dayjs(spark.ends_at))
          : NO_DATE_YET,
      );

    // Try to split on ## headings
    const sectionRegex = /^##\s+(.+)$/gm;
    const sections: Record<string, string> = {};
    let lastKey = "overview";
    let lastIndex = 0;
    let match;

    while ((match = sectionRegex.exec(text)) !== null) {
      const content = text.slice(lastIndex, match.index).trim();
      if (content) {
        sections[lastKey] = content;
      }
      const heading = match[1].toLowerCase().trim();
      if (heading.includes("expect")) {
        lastKey = "whatToExpect";
      } else {
        lastKey = heading;
      }
      lastIndex = match.index + match[0].length;
    }
    const remaining = text.slice(lastIndex).trim();
    if (remaining) {
      sections[lastKey] = remaining;
    }

    return {
      whatToExpect: sections.whatToExpect || "",
    };
  }, [spark]);

  // Highlight images for the 2x2 grid (use event images if available)
  const highlightImages = useMemo<NonNullable<typeof spark>["images"]>(() => {
    const raw = spark?.images;
    let arr: NonNullable<typeof spark>["images"] = [];
    if (Array.isArray(raw)) {
      arr = raw;
    } else if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) arr = parsed;
      } catch {
        /* fall through */
      }
    }
    return arr.slice(0, 4);
  }, [spark?.images]);

  const whenLabel = [dateTimeRange.date, dateTimeRange.time]
    .filter(Boolean)
    .join(" · ");

  const mapsEmbedUrl = useMemo(
    () =>
      sparkMapEmbedUrl({
        location: spark?.location,
        google_maps_place_id: spark?.google_maps_place_id,
      }),
    [spark?.location, spark?.google_maps_place_id],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const shareImage = spark && sparkShareImage(spark);

  const handleShare = async () => {
    if (navigator.share) {
      navigator.share({
        title: spark?.title,
        url: window.location.href,
      });
    } else {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = window.location.href;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      toaster.create({
        type: "success",
        title: "Link copied to clipboard",
      });
    }
  };

  const shareButton = (
    <chakra.button
      type="button"
      onClick={handleShare}
      aria-label="Share"
      bg="transparent"
      border="none"
      cursor="pointer"
      color="fg.default"
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="40px"
      h="40px"
      mr="-2"
    >
      <FontAwesomeIcon icon={isApplePlatform ? faArrowUpFromBracket : faShareNodes} />
    </chakra.button>
  );

  return (
    <ModalPage
      motionKey="event-modal"
      title={spark?.title || ""}
      fallback="/events"
      rightAction={shareButton}
      animateEntrance={animateEntrance}
      bodyBg="bg.canvas"
      header={
        spark && (
          <Meta>
            <title>{spark.title}</title>
            <meta property="og:title" content={spark.title} />
            <meta property="og:type" content="website" />
            <meta
              property="og:url"
              content={`https://www.greenflagdate.com/event/atlanta/${sparkId}`}
            />
            {shareImage && <meta property="og:image" content={shareImage} />}
            {spark.overview_preview && (
              <meta
                property="og:description"
                content={spark.overview_preview}
              />
            )}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={spark.title} />
            {shareImage && <meta name="twitter:image" content={shareImage} />}
            {spark.overview_preview && (
              <meta
                name="twitter:description"
                content={spark.overview_preview}
              />
            )}
          </Meta>
        )
      }
    >
      {/* Hero Image */}
      <Image
        src={sparkReqState.result?.hero_image}
        alt={sparkReqState.result?.title || "Event"}
        w="100%"
        maxW="58rem"
        mx="auto"
        aspectRatio="16/9"
        objectFit="cover"
      />

      <Container
        maxW="58rem"
        px="1.25rem"
        pb={isMobile ? "9rem" : "6rem"}
        mx="auto"
      >
        {/* Event Title - Below Image */}
        <Heading as="h1" textStyle="displayTitle" mt="1.5rem" mb="1rem">
          {spark?.title || ""}
        </Heading>

        {(attendingLikedCount > 0 ||
          inviters.length > 0 ||
          (spark &&
            (registrationType === "registered" ||
              registrationType === "waitlist"))) && (
          <Flex
            data-testid="event-hero-sparks"
            justify="center"
            mt="1rem"
            direction="column"
            align="center"
          >
            {(attendingLikedCount > 0 || inviters.length > 0) && (
              <SparksChip
                count={attendingLikedCount}
                size="md"
                avatars={sparkAvatars}
                inviters={inviters.length > 0 ? inviters : undefined}
              />
            )}
            {registrationType === "registered" && spark && (
              <Box
                ref={setSpotSavedEl}
                mt={attendingLikedCount > 0 || inviters.length > 0 ? "1rem" : "0"}
                w="100%"
              >
                <SpotSavedWidget
                  event={spark}
                  location={spark.location_address || spark.location}
                  description={window.location.href}
                  onCancelled={refresh}
                  soon={eventSoon}
                  checkInCode={
                    myRegistration
                      ? {
                          token: myRegistration.check_in_token,
                          onShow: openCheckIn,
                          checkedIn: !!myRegistration.checked_in_at,
                        }
                      : undefined
                  }
                  preSpark={preSpark}
                  onStartSparking={startSparking}
                />
              </Box>
            )}
            {registrationType === "waitlist" && spark && (
              <Box
                mt={attendingLikedCount > 0 || inviters.length > 0 ? "1rem" : "0"}
                w="100%"
              >
                <SpotWaitlistedWidget
                  sparkId={spark.id}
                  onLeftWaitlist={refresh}
                />
              </Box>
            )}
          </Flex>
        )}

        {myRegistration?.arrival && (
          <EventArrivalSection arrival={myRegistration.arrival} />
        )}

        {/* Event Details */}
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }}
          justifyContent="center"
          gap="1.2rem"
          columnGap={{ sm: "2.5rem" }}
          mt="1.25rem"
        >
          <DetailRow
            icon={faCalendar}
            primary={dateTimeRange.date}
            secondary={dateTimeRange.time}
          />
          <DetailRow
            icon={faLocationDot}
            primary={spark?.location || ""}
            secondary={spark?.location_address || undefined}
            onClick={() => {
              document
                .getElementById("location-section")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          />
          <DetailRow
            icon={faUser}
            primary={
              spark ? getAgeRangeString(spark.min_age, spark.max_age, spark.age_range_label).display : ""
            }
          />
          <DetailRow
            icon={faTicketSimple}
            primary={
              spark
                ? activeDiscount
                  ? `$${activeDiscount.price}`
                  : priceLabel(spark.price)
                : ""
            }
            secondary={spark && activeDiscount ? `$${spark.price}` : undefined}
            strikeSecondary={!!activeDiscount}
            badge={
              activeDiscount
                ? `${activeDiscount.label}${activeDiscount.expires_at ? ` ends ${dayjs(activeDiscount.expires_at).format("MMM D")}` : ""}`
                : undefined
            }
          />
        </Box>

        {myRegistration && (
          <Box mt="1.5rem">
            <AfterEventMatchingCard />
          </Box>
        )}

        <SectionDivider mt="1.5rem" />

        {/* Overview Section - not collapsible */}
        {spark?.overview_preview && (
          <Box mb="1.5rem">
            <Text textStyle="sectionTitle" mb="0.5rem">
              Overview
            </Text>
            <StandardMarkdown content={spark.overview_preview} />
            {overviewExpanded && spark.overview_read_more ? (
              <>
                <StandardMarkdown content={spark.overview_read_more} />
                <Text
                  as="span"
                  textStyle="textLink"
                  cursor="pointer"
                  onClick={() => setOverviewExpanded(false)}
                >
                  Show Less{" "}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    style={{ fontSize: "10px", transform: "rotate(180deg)" }}
                  />
                </Text>
              </>
            ) : spark.overview_read_more ? (
              <Text
                as="span"
                textStyle="textLink"
                cursor="pointer"
                onClick={() => setOverviewExpanded(true)}
              >
                Read More{" "}
                <FontAwesomeIcon
                  icon={faChevronRight}
                  style={{ fontSize: "10px" }}
                />
              </Text>
            ) : null}
          </Box>
        )}

        <SectionDivider showIf={!!spark?.overview_preview} />

        {/* What's Included Section - show all, no Read More */}
        {spark?.whats_included && (
          <Box mb="1.5rem">
            <Text textStyle="sectionTitle" mb="0.5rem">
              What's Included
            </Text>
            <StandardMarkdown content={spark.whats_included} />
          </Box>
        )}

        <SectionDivider showIf={!!spark?.whats_included} />

        {/* Past Event Highlights - horizontal swipable */}
        {highlightImages.length > 0 && (
          <Box mt="1.5rem" mb="1.5rem">
            <Text textStyle="sectionTitle" mb="0.75rem">
              Past Event Highlights
            </Text>
            <Swiper
              spaceBetween={12}
              slidesPerView={2.3}
              modules={[FreeMode]}
              freeMode
              breakpoints={{
                480: { slidesPerView: 3.3 },
              }}
            >
              {highlightImages.map((img, index) => (
                <SwiperSlide key={index}>
                  <Image
                    src={img.url}
                    alt={img.alternativeText || `Highlight ${index + 1}`}
                    w="100%"
                    aspectRatio="1/1"
                    objectFit="cover"
                    borderRadius="0.5rem"
                    cursor="pointer"
                    onClick={() => setLightboxIndex(index)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        )}

        <SectionDivider showIf={highlightImages.length > 0} />

        {/* Accordion Sections */}
        <Box>
          {descriptionSections.whatToExpect && (
            <AccordionSection title="What to Expect">
              <StandardMarkdown content={descriptionSections.whatToExpect} />
            </AccordionSection>
          )}
          {descriptionSections.whatToExpect && (
            <Box borderTop="1px solid" borderColor="brand.green.dark" />
          )}
        </Box>

        {/* Location Section with Map */}
        {spark?.location && (
          <Box mt="2rem" id="location-section">
            <Text textStyle="sectionTitle" mb="0.5rem">
              Location
            </Text>
            <Text textStyle="body">{spark.location}</Text>
            {spark.location_address && (
              <Text textStyle="bodyMuted">{spark.location_address}</Text>
            )}
            <Link
              href={googleMapsLink(
                spark.location_address || spark.location,
                spark.google_maps_place_id,
              )}
              target="_blank"
              rel="noopener noreferrer"
              textStyle="textLink"
              color="fg.onTint"
              textDecoration="underline"
              mt="0.25rem"
            >
              Get directions
            </Link>
            {mapsEmbedUrl && (
              <Box
                mt="0.75rem"
                borderRadius="0.5rem"
                overflow="hidden"
                h="300px"
              >
                <iframe
                  title="Event location map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={mapsEmbedUrl}
                />
              </Box>
            )}
          </Box>
        )}

        {myRegistration && spark && (
          <>
            <SectionDivider mt="2rem" />
            <RegistrationReceipt
              eventTitle={spark.title}
              registeredAt={myRegistration.registered_at}
              price={myRegistration.price}
            />
          </>
        )}
      </Container>

      {/* Lightbox */}
      {lightboxIndex !== null && sparkReqState.result?.images && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.800"
          zIndex={1000}
          align="center"
          justify="center"
          onClick={() => setLightboxIndex(null)}
        >
          <Flex
            position="absolute"
            top="1rem"
            right="1rem"
            w="2.5rem"
            h="2.5rem"
            align="center"
            justify="center"
            cursor="pointer"
            color="white"
            zIndex={1001}
          >
            <FontAwesomeIcon icon={faXmark} size="xl" />
          </Flex>
          {highlightImages.length > 1 && (
            <>
              <Flex
                position="absolute"
                left="1rem"
                top="50%"
                transform="translateY(-50%)"
                w="2.5rem"
                h="2.5rem"
                bg="whiteAlpha.300"
                borderRadius="50%"
                align="center"
                justify="center"
                cursor="pointer"
                color="white"
                zIndex={1001}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev !== null
                      ? (prev - 1 + highlightImages.length) %
                        highlightImages.length
                      : null,
                  );
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Flex>
              <Flex
                position="absolute"
                right="1rem"
                top="50%"
                transform="translateY(-50%)"
                w="2.5rem"
                h="2.5rem"
                bg="whiteAlpha.300"
                borderRadius="50%"
                align="center"
                justify="center"
                cursor="pointer"
                color="white"
                zIndex={1001}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev !== null
                      ? (prev + 1) % highlightImages.length
                      : null,
                  );
                }}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Flex>
            </>
          )}
          <Image
            src={highlightImages[lightboxIndex]?.url}
            alt={
              highlightImages[lightboxIndex]?.alternativeText ||
              `Event photo ${lightboxIndex + 1}`
            }
            maxH="90vh"
            maxW="90vw"
            objectFit="contain"
            onClick={(e) => e.stopPropagation()}
          />
        </Flex>
      )}

      {/* Pinned Bottom Bar */}
      {/* Shown to anyone who can still register: unregistered users, and
          waitlisted users once a spot has opened up for their gender
          (registrationType stays "waitlist" until they re-register, so gate
          on !isSparkFull rather than hiding the bar for all waitlisters). */}
      {spark &&
        registrationType !== "registered" &&
        (registrationType !== "waitlist" || !isSparkFull) && (
        <PinnedBottomBar>
          <Box>
            <Flex align="center" gap="0.5rem">
              <Text textStyle="bodyLg" fontWeight="700">
                {activeDiscount
                  ? `$${activeDiscount.price}`
                  : priceLabel(spark.price)}
              </Text>
              {activeDiscount && (
                <Text
                  textStyle="body"
                  color="brand.gray.primary"
                  textDecoration="line-through"
                >
                  ${spark.price}
                </Text>
              )}
            </Flex>
            {activeDiscount && (
              <Text
                as="span"
                display="inline-block"
                bg="orange.subtle"
                color="orange.fg"
                fontSize="0.7rem"
                fontWeight="600"
                px="0.5rem"
                py="0.1rem"
                borderRadius="full"
                mt="0.15rem"
              >
                {activeDiscount.label}
                {activeDiscount.expires_at &&
                  ` ends ${dayjs(activeDiscount.expires_at).format("MMM D")}`}
              </Text>
            )}
            <Text textStyle="caption" color="brand.neutral.dark">
              {whenLabel}
            </Text>
          </Box>
          <Button
            variant="solid"
            colorPalette="greenLight"
            py="0.75rem"
            px="2rem"
            fontSize="1rem"
            size="lg"
            onClick={() => setDrawerOpen(true)}
          >
            Get Tickets
          </Button>
        </PinnedBottomBar>
      )}

      {spark && myRegistration && !spotSavedInView && (
        <RegisteredEventBar
          when={whenLabel}
          // The code only from the last few hours, as in the card above.
          onShowCheckInCode={eventSoon ? openCheckIn : undefined}
          onStartSparking={showPreSpark && !eventSoon ? startSparking : undefined}
        />
      )}
      {spark && myRegistration && (
        <CheckInCodeSheet
          open={checkInOpen}
          onClose={closeCheckIn}
          eventTitle={spark.title}
          when={whenLabel}
          location={spark.location}
          registration={myRegistration}
        />
      )}

      {/* Registration Drawer */}
      {sparkReqState.result && (
        <DrawerRoot
          open={drawerOpen}
          onOpenChange={(details) => {
            if (!details.open) setDrawerOpen(false);
          }}
          placement="bottom"
        >
          <DrawerBackdrop />
          <DrawerPositioner
            pb={keyboardInset ? `${keyboardInset}px` : undefined}
            transition="padding-bottom 150ms ease"
          >
            <DrawerContent
              borderTopRadius="1rem"
              maxH={`calc(90dvh - ${keyboardInset}px)`}
              maxW="58rem"
              mx="auto"
              display="flex"
              flexDirection="column"
            >
              <Flex
                justify="space-between"
                align="center"
                px="1.5rem"
                pt="1.25rem"
                pb="0.75rem"
                borderBottom="1px solid"
                borderColor="brand.neutral.medium"
              >
                <Heading as="h2" textStyle="headingBold">
                  {registrationType === "registered"
                    ? "You're registered!"
                    : isSparkFull && registrationType === "waitlist"
                      ? "You're on the wait list!"
                      : "Join the event"}
                </Heading>
                <Flex
                  as="button"
                  align="center"
                  justify="center"
                  w="2rem"
                  h="2rem"
                  borderRadius="full"
                  bg="brand.neutral.light"
                  border="0"
                  cursor="pointer"
                  flexShrink={0}
                  onClick={() => setDrawerOpen(false)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Flex>
              </Flex>
              <DrawerBody
                p="0"
                flex="1"
                minH="0"
                display="flex"
                flexDirection="column"
                overflow="hidden"
              >
                <SparkRegisterForm
                  spark={sparkReqState.result}
                  onRegister={() => {
                    refresh();
                    setDrawerOpen(false);
                    void sendPendingInviteAfterRegister(
                      sparkReqState.result!.id,
                    );
                    profileReminderPendingRef.current = !Object.keys(
                      storeData.user?.media || {},
                    ).length;
                    // Someone who arrived with invite intent just had that
                    // invite sent for them — asking them to invite a spark
                    // again would be asking twice. Straight to the profile
                    // nudge for them.
                    if (pendingInvite) {
                      showProfileReminderIfPending();
                    } else {
                      setInvitePromptOpen(true);
                    }
                  }}
                  onJoinWaitlist={() => {
                    refresh();
                    setDrawerOpen(false);
                  }}
                />
              </DrawerBody>
            </DrawerContent>
          </DrawerPositioner>
        </DrawerRoot>
      )}
      <InviteSparkAfterRegisterDialog
        open={invitePromptOpen}
        spark={spark ?? null}
        onClose={closeInvitePrompt}
        onNothingToInvite={closeInvitePrompt}
      />
      <CompleteProfileReminderDialog
        open={profileReminderShown}
        onOpenChange={setProfileReminderShown}
      />
    </ModalPage>
  );
}

function DetailRow({
  icon,
  primary,
  secondary,
  strikeSecondary,
  badge,
  onClick,
}: {
  icon: typeof faCalendar;
  primary: string;
  secondary?: string;
  strikeSecondary?: boolean;
  badge?: string;
  onClick?: () => void;
}) {
  const [greenDark] = useToken("colors", ["brand.green.dark"]);

  const content = (
    <>
      <HStack gap="1.2rem" align="center">
        <Flex w="1.3rem" flexShrink={0} justify="center">
          <FontAwesomeIcon
            icon={icon}
            color={greenDark}
            style={{ fontSize: "1.3rem" }}
          />
        </Flex>
        <Flex align="center" gap="0.5rem">
          <Text textStyle="body">{primary}</Text>
          {secondary && strikeSecondary && (
            <Text textStyle="bodyMuted" textDecoration="line-through">
              {secondary}
            </Text>
          )}
        </Flex>
      </HStack>
      {secondary && !strikeSecondary && (
        <Text textStyle="bodyMuted" mt="-3px" ps="2.5rem">
          {secondary}
        </Text>
      )}
      {badge && (
        <Text
          as="span"
          display="inline-block"
          bg="orange.subtle"
          color="orange.fg"
          fontSize="0.7rem"
          fontWeight="600"
          px="0.5rem"
          py="0.1rem"
          borderRadius="full"
          mt="0.15rem"
          ms="2.5rem"
        >
          {badge}
        </Text>
      )}
    </>
  );

  if (onClick) {
    return (
      <Box
        css={{
          "&:hover": { opacity: 0.7 },
          cursor: "pointer",
        }}
        onClick={onClick}
      >
        {content}
      </Box>
    );
  }

  return <Box>{content}</Box>;
}
